import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  QrCode, Smartphone, ChefHat, CreditCard, HeartHandshake, 
  Sparkles, CalendarCheck, Package, ChevronDown, ChevronUp, Zap,
  Gift, FileText, SlidersHorizontal
} from 'lucide-react';
import './QRMandaPage.css';

const QRMandaPage = ({ whatsappNumber = "525624480446" }) => {
  const [faqAbierta, setFaqAbierta] = useState(null);

  const toggleFaq = (idx) => {
    setFaqAbierta(prev => (prev === idx ? null : idx));
  };

  const handleContratar = (plan, precio) => {
    const msg = encodeURIComponent(
      `¡Hola! Deseo contratar el ${plan} ($${precio} MXN IVA incl.) de QRManda para mi restaurante.`
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${msg}`, '_blank');
  };

  const faqs = [
    {
      q: '¿Qué son los 4 Atajos Rápidos del Menú Móvil?',
      a: 'Es una barra superior de acceso veloz en la carta digital del comensal. Puedes activar hasta 4 botones inteligentes para que el cliente solicite su factura, reserve mesa, abra la ubicación en Google Maps, escriba al WhatsApp del negocio o abra cualquier enlace externo con un solo toque.'
    },
    {
      q: '¿Cómo funciona la facturación automática en el Plan Élite?',
      a: 'Tus comensales ingresan el folio de su ticket y su RFC desde el menú en su celular. El sistema valida el consumo contra el pedido real en sala, emite la factura CFDI 4.0 ante el SAT y les envía de inmediato sus archivos XML y PDF por correo electrónico.'
    },
    {
      q: '¿Qué pasa si mi restaurante supera las 150 facturas mensuales incluidas en el Plan Élite?',
      a: 'Tu operación no se detiene en ningún momento. Puedes adquirir bolsas de timbres adicionales a un costo muy accesible y seguir emitiendo comprobantes sin complicaciones.'
    },
    {
      q: '¿Cómo funciona el sistema de reservaciones con anticipo?',
      a: 'Tus comensales eligen fecha, hora y número de personas desde tu enlace web. Puedes solicitar un anticipo o depósito de garantía opcional vía Mercado Pago para apartar la mesa, eliminando las cancelaciones de último momento (no-shows).'
    },
    {
      q: '¿Qué es el Auto-Pedido en mesa (Self-Ordering)?',
      a: 'El cliente se sienta en la mesa, escanea el código QR y puede ordenar platillos directamente desde su celular a la cocina sin necesidad de esperar a que el mesero se acerque con la comanda física.'
    },
    {
      q: '¿Necesito comprar equipo especial para usar QRManda?',
      a: 'No. El sistema opera 100% en la nube en cualquier tablet, smartphone o computadora con navegador web. Para cobro con tarjeta, se vincula de manera nativa con terminales físicas Mercado Pago Point.'
    }
  ];

  return (
    <div className="qrmanda-full-page">
      {/* HERO SECTION */}
      <section className="qrmanda-hero-section">
        <div className="qrmanda-glow-orb"></div>

        <motion.div 
          className="qrmanda-hero-box"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="qrmanda-badge-capsule">
            <Sparkles size={14} color="#00d2ff" />
            <span>SISTEMA PARA RESTAURANTES, CAFETERÍAS & BARES</span>
          </div>

          <h1 className="qrmanda-page-title">
            Digitaliza tu servicio con <span className="text-cyan">QR<span>Manda</span></span>
          </h1>

          <p className="qrmanda-page-subtitle">
            Conecta la mesa, la cocina, reservaciones, facturación automática CFDI 4.0 y caja en tiempo real. Elimina las comandas en papel, agiliza tiempos de despacho y cobra con terminal en un solo ecosistema respaldado por 3 Vértices.
          </p>

          <div className="qrmanda-hero-actions">
            <a href="#planes" className="cta-button primary">Ver Planes de Contratación</a>
            <a href="#caracteristicas" className="cta-button secondary">Explorar Módulos</a>
          </div>
        </motion.div>
      </section>

      {/* MÓDULOS DE OPERACIÓN CON IMÁGENES ALUSIVAS Y DE LIBRE USO */}
      <section id="caracteristicas" className="qrmanda-features-section">
        <div className="features-head-intro">
          <span className="section-subtitle-tag">ECOSISTEMA MODULAR COMPLETO</span>
          <h2>Herramientas diseñadas para cada área de tu restaurante</h2>
          <p>Identifica qué funciones incluye cada nivel de suscripción según el tamaño y flujo de tu operación.</p>
        </div>

        <div className="qrmanda-features-grid">
          
          {/* MÓDULO 1: MENÚ QR CON DIRECCIÓN Y HORARIOS */}
          <div className="qrmanda-feature-card">
            <div className="module-tier-tag basic">INCLUIDO EN PLAN BÁSICO</div>
            <div className="card-media-preview">
              <img 
                src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=650&auto=format&fit=crop&q=80" 
                alt="Restaurante y mesa con menú digital" 
                className="module-img"
              />
            </div>
            <div className="card-info-content">
              <div className="qrmanda-icon-wrap qr"><QrCode size={24} /></div>
              <h3>Menú QR con Ubicación & Horarios</h3>
              <p>Carta digital HD con fotografías, turnos flexibles de atención, redes sociales oficiales y dirección desglosada con botón interactivo "Ir" hacia Google Maps.</p>
            </div>
          </div>

          {/* MÓDULO 2: CAPTURA DE COMANDAS */}
          <div className="qrmanda-feature-card highlight">
            <div className="module-tier-tag premium">PLAN PREMIUM & ÉLITE</div>
            <div className="card-media-preview">
              <img 
                src="https://images.unsplash.com/photo-1552566626-52f8b828add9?w=650&auto=format&fit=crop&q=80" 
                alt="Mesero tomando comanda en mesa" 
                className="module-img"
              />
            </div>
            <div className="card-info-content">
              <div className="qrmanda-icon-wrap pos"><Smartphone size={24} /></div>
              <h3>Captura de Comandas por Meseros</h3>
              <p>Toma de pedidos en tablets o celulares de meseros, asignación por mesa, modificadores de platillo y protección de cancelaciones con <strong>PIN de Gerente</strong>.</p>
            </div>
          </div>

          {/* MÓDULO 3: VISTA EN COCINA */}
          <div className="qrmanda-feature-card">
            <div className="module-tier-tag premium">PLAN PREMIUM & ÉLITE</div>
            <div className="card-media-preview">
              <img 
                src="https://images.unsplash.com/photo-1577219491135-ce391730fb2c?w=650&auto=format&fit=crop&q=80" 
                alt="Cocineros en cocina de restaurante" 
                className="module-img"
              />
            </div>
            <div className="card-info-content">
              <div className="qrmanda-icon-wrap kds"><ChefHat size={24} /></div>
              <h3>Vista de Pedidos en Cocina</h3>
              <p>Pantalla táctil para cocineros con alertas de nuevas órdenes, tiempo de preparación por mesa y semáforo visual de retrasos para acelerar el despacho.</p>
            </div>
          </div>

          {/* MÓDULO 4: FACTURACIÓN CFDI Y BUZÓN */}
          <div className="qrmanda-feature-card">
            <div className="module-tier-tag premium">PLAN PREMIUM & ÉLITE</div>
            <div className="card-media-preview">
              <img 
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=650&auto=format&fit=crop&q=80" 
                alt="Facturación y cuentas de restaurante" 
                className="module-img"
              />
            </div>
            <div className="card-info-content">
              <div className="qrmanda-icon-wrap pay"><FileText size={24} /></div>
              <h3>Buzón & Facturación Electrónica</h3>
              <p>Formulario para comensales en el menú móvil con validación de tickets. En Plan Pro copias los datos al SAT y en Plan Élite timbra y envía XML/PDF automáticamente.</p>
            </div>
          </div>

          {/* MÓDULO 5: TERMINALES POINT SMART (IMAGEN ACTUALIZADA Y ESTABLE) */}
          <div className="qrmanda-feature-card">
            <div className="module-tier-tag premium">PLAN PREMIUM & ÉLITE</div>
            <div className="card-media-preview">
              <img 
                src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=650&auto=format&fit=crop&q=80" 
                alt="Cobro con tarjeta y terminal bancaria" 
                className="module-img"
              />
            </div>
            <div className="card-info-content">
              <div className="qrmanda-icon-wrap pay"><CreditCard size={24} /></div>
              <h3>Integración con Terminales Point</h3>
              <p>Envío directo del total de la comanda a terminales físicas Mercado Pago Point, control de efectivo y cálculo automático de propinas sugeridas.</p>
            </div>
          </div>

          {/* MÓDULO 6: BARRA DE ATAJOS RÁPIDOS */}
          <div className="qrmanda-feature-card elite-highlight">
            <div className="module-tier-tag elite">EXCLUSIVO PLAN ÉLITE</div>
            <div className="card-media-preview">
              <img 
                src="https://images.unsplash.com/photo-1559339352-11d035aa65de?w=650&auto=format&fit=crop&q=80" 
                alt="Comensal usando celular en mesa" 
                className="module-img"
              />
            </div>
            <div className="card-info-content">
              <div className="qrmanda-icon-wrap self"><SlidersHorizontal size={24} /></div>
              <h3>4 Atajos Rápidos en Menú Móvil</h3>
              <p>Barra superior con botones directos y personalizados: Reservar Mesa, Facturar Ticket, Cómo Llegar (Google Maps) o chat de WhatsApp de atención al cliente.</p>
            </div>
          </div>

          {/* MÓDULO 7: RESERVACIONES CON ANTICIPO */}
          <div className="qrmanda-feature-card elite-highlight">
            <div className="module-tier-tag elite">EXCLUSIVO PLAN ÉLITE</div>
            <div className="card-media-preview">
              <img 
                src="https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?w=650&auto=format&fit=crop&q=80" 
                alt="Mesa elegante reservada en restaurante" 
                className="module-img"
              />
            </div>
            <div className="card-info-content">
              <div className="qrmanda-icon-wrap res"><CalendarCheck size={24} /></div>
              <h3>Reservaciones con Anticipo</h3>
              <p>Agenda interactiva de mesas con depósito de garantía online para evitar comensales que no llegan (no-shows) y confirmaciones directas por WhatsApp.</p>
            </div>
          </div>

          {/* MÓDULO 8: AUTO-PEDIDO EN MESA */}
          <div className="qrmanda-feature-card elite-highlight">
            <div className="module-tier-tag elite">EXCLUSIVO PLAN ÉLITE</div>
            <div className="card-media-preview">
              <img 
                src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=650&auto=format&fit=crop&q=80" 
                alt="Comida servida en mesa de restaurante" 
                className="module-img"
              />
            </div>
            <div className="card-info-content">
              <div className="qrmanda-icon-wrap self"><Zap size={24} /></div>
              <h3>Auto-Pedido (Self-Ordering)</h3>
              <p>Tus comensales piden directamente desde su celular escaneando el QR de su mesa. Ideal para agilizar comandas y rotar mesas más rápido en horas pico.</p>
            </div>
          </div>

          {/* MÓDULO 9: CONTROL DE PORCIONES Y STOCK */}
          <div className="qrmanda-feature-card elite-highlight">
            <div className="module-tier-tag elite">EXCLUSIVO PLAN ÉLITE</div>
            <div className="card-media-preview">
              <img 
                src="https://images.unsplash.com/photo-1541544741938-0af808871cc0?w=650&auto=format&fit=crop&q=80" 
                alt="Ingredientes frescos de cocina" 
                className="module-img"
              />
            </div>
            <div className="card-info-content">
              <div className="qrmanda-icon-wrap stock"><Package size={24} /></div>
              <h3>Control de Porciones & Stock Bajo</h3>
              <p>Descuento automático de porciones por comanda despachada y apagado automático en la carta móvil de aquellos platillos que se han agotado.</p>
            </div>
          </div>

          {/* MÓDULO 10: CLUB DE MEMBRESÍA O PUNTOS */}
          <div className="qrmanda-feature-card elite-highlight">
            <div className="module-tier-tag elite">EXCLUSIVO PLAN ÉLITE</div>
            <div className="card-media-preview">
              <img 
                src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=650&auto=format&fit=crop&q=80" 
                alt="Clientes disfrutando en restaurante" 
                className="module-img"
              />
            </div>
            <div className="card-info-content">
              <div className="qrmanda-icon-wrap loyalty"><Gift size={24} /></div>
              <h3>Club de Membresía o Puntos Rewards</h3>
              <p>Acumulación de puntos por número de teléfono sin tarjetas plásticas. Fideliza comensales y premia sus visitas frecuentes a tu establecimiento.</p>
            </div>
          </div>

          {/* MÓDULO 11: AUDITORÍA & ENCUESTAS */}
          <div className="qrmanda-feature-card">
            <div className="module-tier-tag premium">PLAN PREMIUM & ÉLITE</div>
            <div className="card-media-preview">
              <img 
                src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=650&auto=format&fit=crop&q=80" 
                alt="Platillos gourmet servidos en mesa" 
                className="module-img"
              />
            </div>
            <div className="card-info-content">
              <div className="qrmanda-icon-wrap poll"><HeartHandshake size={24} /></div>
              <h3>Auditoría, Encuestas & Cortes PDF</h3>
              <p>Métricas reales de satisfacción CSAT, ranking de colaboradores de salón, curva horaria y emisión de corte de caja en PDF para arqueo diario.</p>
            </div>
          </div>

        </div>
      </section>

      {/* PLANES A LA MEDIDA */}
      <section id="planes" className="qrmanda-pricing-container">
        <div className="pricing-title-wrap">
          <h2>Planes a la Medida de tu Negocio</h2>
          <p>Digitaliza y automatiza tu sucursal con el respaldo de 3 Vértices.</p>
        </div>

        <div className="pricing-three-cards-grid">
          
          {/* 1. PLAN MENÚ BÁSICO ($249) */}
          <div className="official-card basic">
            <div className="pill-category">CATÁLOGO, UBICACIÓN & HORARIOS</div>
            
            <h3 className="card-plan-name">Plan Menú Básico</h3>
            <p className="card-plan-for">Ideal para cafeterías, taquerías y negocios locales.</p>
            
            <div className="price-number-row">
              <span className="price-value">$249</span>
              <span className="price-unit">MXN / mes</span>
            </div>
            
            <div className="iva-badge">IVA incluido</div>

            <ul className="plan-points-list">
              <li><span className="ico-check">✅</span> Menú QR dinámico con tu propio enlace</li>
              <li><span className="ico-check">✅</span> <strong>Dirección desglosada con botón "Ir" a Google Maps</strong></li>
              <li><span className="ico-check">✅</span> <strong>Horarios de atención flexibles (múltiples turnos)</strong></li>
              <li><span className="ico-check">✅</span> <strong>Redes sociales oficiales con iconos enlazados</strong></li>
              <li><span className="ico-check">✅</span> Gestión ilimitada de platillos y fotos</li>
              <li><span className="ico-check">✅</span> Configuración de turnos (Desayuno, Comida, Cena)</li>
              <li><span className="ico-check">✅</span> Métricas esenciales y corte de caja en PDF</li>
            </ul>

            <button 
              type="button" 
              className="btn-hire basic"
              onClick={() => handleContratar('Plan Menú Básico', '249')}
            >
              Contratar Plan Básico ($249 IVA incl.)
            </button>
          </div>

          {/* 2. PLAN OPERATIVO PREMIUM ($589) */}
          <div className="official-card premium">
            <div className="ribbon-integral-badge">OPERACIÓN & FACTURAS</div>
            
            <div className="pill-category blue">SISTEMA OPERATIVO & FACTURACIÓN</div>
            
            <h3 className="card-plan-name">Plan Operativo Premium</h3>
            <p className="card-plan-for">Para salones con meseros, alta rotación y cocina.</p>
            
            <div className="price-number-row">
              <span className="price-value blue-val">$589</span>
              <span className="price-unit">MXN / mes</span>
            </div>
            
            <div className="iva-badge">IVA incluido</div>

            <ul className="plan-points-list">
              <li><span className="ico-star">⭐</span> <strong>Todo lo incluido en el Plan Básico</strong></li>
              <li><span className="ico-star">⭐</span> <strong>Formulario para solicitud de facturación</strong></li>
              <li><span className="ico-star">⭐</span> <strong>Integración con terminales Mercado Pago Point</strong></li>
              <li><span className="ico-star">⭐</span> <strong>Sistema para captura de comandas por parte de los meseros</strong></li>
              <li><span className="ico-star">⭐</span> <strong>Vista de pedidos en cocina</strong></li>
              <li><span className="ico-star">⭐</span> <strong>Encuestas de satisfacción y métricas CSAT</strong></li>
              <li><span className="ico-star">⭐</span> <strong>3 roles activos (Admin, Cocina, Mesero)</strong></li>
            </ul>

            <button 
              type="button" 
              className="btn-hire premium"
              onClick={() => handleContratar('Plan Operativo Premium', '589')}
            >
              Contratar Plan Premium ($589 IVA incl.)
            </button>
          </div>

          {/* 3. PLAN ÉLITE & RESERVAS ($1,089) */}
          <div className="official-card elite">
            <div className="ribbon-integral-badge elite">SUITE COMPLETA & SAT</div>
            
            <div className="pill-category purple">TIMBRADO SAT & AUTOMATIZACIÓN</div>
            
            <h3 className="card-plan-name">Plan Élite & Reservas</h3>
            <p className="card-plan-for">Para restaurantes con facturación directa, reservas y lealtad.</p>
            
            <div className="price-number-row">
              <span className="price-value purple-val">$1,089</span>
              <span className="price-unit">MXN / mes</span>
            </div>
            
            <div className="iva-badge">IVA incluido</div>

            <ul className="plan-points-list">
              <li><span className="ico-star">⭐</span> <strong>Todo lo incluido en el Plan Premium</strong></li>
              <li><span className="ico-zap">⚡</span> <strong>Facturación Automática CFDI 4.0 (150 facturas/mes incluidas, paquetes adicionales disponibles)</strong></li>
              <li><span className="ico-zap">⚡</span> <strong>Envío directo del XML y PDF oficial por correo al comensal</strong></li>
              <li><span className="ico-zap">⚡</span> <strong>Barra de 4 Atajos Rápidos Personalizables en la carta móvil</strong></li>
              <li><span className="ico-zap">⚡</span> <strong>Reservaciones online con anticipo obligatorio anti-no-show</strong></li>
              <li><span className="ico-zap">⚡</span> <strong>Auto-pedido en mesa desde el QR (Self-Ordering)</strong></li>
              <li><span className="ico-zap">⚡</span> <strong>Control de porciones y semáforo automático de stock bajo</strong></li>
              <li><span className="ico-zap">⚡</span> <strong>Club de membresía o puntos rewards</strong></li>
            </ul>

            <button 
              type="button" 
              className="btn-hire elite"
              onClick={() => handleContratar('Plan Élite & Reservas', '1,089')}
            >
              Contratar Plan Élite ($1,089 IVA incl.)
            </button>
          </div>

        </div>
      </section>

      {/* PREGUNTAS FRECUENTES */}
      <section className="qrmanda-faq-container">
        <div className="pricing-title-wrap">
          <h2>Preguntas Frecuentes</h2>
          <p>Todo lo que necesitas saber antes de implementar QRManda en tu sucursal.</p>
        </div>

        <div className="qrmanda-faq-list">
          {faqs.map((f, i) => (
            <div key={i} className={`faq-card-row ${faqAbierta === i ? 'open' : ''}`}>
              <button 
                type="button" 
                className="faq-row-trigger"
                onClick={() => toggleFaq(i)}
              >
                <span>{f.q}</span>
                {faqAbierta === i ? <ChevronUp size={18} color="#00d2ff" /> : <ChevronDown size={18} />}
              </button>
              {faqAbierta === i && (
                <div className="faq-row-answer">
                  <p>{f.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default QRMandaPage;